/**
 * The sync engine.
 *
 * Drains the outbox in **one request** — `POST /sync/push` carrying the whole
 * claimed batch, which answers with a verdict per operation.
 *
 * The endpoint is a transport, not a second implementation. Each operation is
 * dispatched server-side to the same service the ordinary endpoint calls, through
 * the same idempotency machinery, so there is exactly one place that knows how a
 * sale is priced and how stock moves. An earlier version replayed each operation
 * against its own ordinary endpoint, which had the same property and cost N round
 * trips: on a township 3G link with a two-second latency and a habit of dropping,
 * twenty sales was twenty chances to fail.
 *
 * Two decisions live here, and getting either wrong is expensive:
 *
 *   the batch request failed      → nothing was judged. Everything is retried,
 *                                   because "the server never answered" is not
 *                                   the same as "the server said no".
 *   an operation was REJECTED     → a conflict. Repeating it will never work and
 *                                   a person has to look.
 *   APPLIED or DUPLICATE          → success. A duplicate means the server already
 *                                   had it, which is what the terminal wanted.
 *
 * Retrying a rejection forever burns a prepaid data bundle; giving up on an
 * unanswered request loses a day's takings.
 */

import { ApiError, NetworkError, api } from "@/api/client";
import { db, getSyncMeta, updateSyncMeta } from "@/db/database";
import { deviceRepository } from "@/repositories/deviceRepository";
import { localStockRepository } from "@/repositories/localStockRepository";
import type { OutboxOperation } from "@/db/schema";
import { outboxRepository } from "@/repositories/outboxRepository";

export interface SyncResult {
  attempted: number;
  acknowledged: number;
  retried: number;
  conflicted: number;
}

/** One verdict from `POST /sync/push`. */
interface OperationResult {
  op_id: string;
  outcome: "APPLIED" | "DUPLICATE" | "REJECTED";
  entity_id: string | null;
  detail: string;
  error_type: string;
}

interface PushResponse {
  batch_id: string;
  server_time: string;
  results: OperationResult[];
  applied: number;
  duplicates: number;
  rejected: number;
}

let running = false;

/**
 * Push everything due.
 *
 * Guarded against re-entry: the engine is triggered by a timer, by the
 * "connection restored" event and by finishing a sale, and two overlapping runs
 * would both claim work and double the request count for no benefit.
 */
export async function push(): Promise<SyncResult> {
  const result: SyncResult = { attempted: 0, acknowledged: 0, retried: 0, conflicted: 0 };
  if (running) return result;

  // Who is sending. A signed-in session with a real token sends as itself,
  // as it always has. Without one — nobody signed in, a session opened offline
  // that has no token yet, a login lapsed after weeks without signal — a
  // paired till sends on its own device token, and the server runs each
  // operation as the cashier it names (`apps/sync/services.apply_device_batch`).
  const session = await db.auth.get("current");
  const asUser = Boolean(session && !session.offline && session.accessToken);
  const deviceToken = asUser ? null : await deviceRepository.getToken();
  if (!asUser && !deviceToken) return result;

  running = true;
  try {
    let claimed = await outboxRepository.claimDue();

    if (!asUser) {
      // Work queued before operations named their cashier cannot be credited
      // to anybody on the till's credential alone. It waits, uncounted, for
      // somebody to sign in.
      const unnamed = claimed.filter((operation) => !operation.payload.cashier_id);
      if (unnamed.length > 0) {
        await outboxRepository.release(unnamed.map((operation) => operation.opId));
        claimed = claimed.filter((operation) => operation.payload.cashier_id);
      }
    }

    result.attempted = claimed.length;
    if (claimed.length === 0) return result;

    // **One request for the whole queue.** On a good connection the difference
    // is a rounding error; on a township 3G link with a two-second round trip
    // and a habit of dropping, twenty sales was twenty chances to fail and
    // twenty sets of TLS overhead against a prepaid bundle.
    //
    // The order is the claim order, which is `sequence` order, and the server
    // does not reorder: a sale must not be applied before the shift it belongs
    // to.
    const meta = await getSyncMeta();
    let response: PushResponse;

    try {
      response = await api.post<PushResponse>(
        asUser ? "/sync/push/" : "/sync/device-push/",
        {
          operations: claimed.map((operation) => ({
            op_id: operation.opId,
            type: operation.type,
            idempotency_key: operation.idempotencyKey,
            sequence: operation.sequence,
            payload: operation.payload,
          })),
          queue_depth: await db.pendingOperationCount(),
          clock_skew_ms: meta.clockSkewMs,
          app_version: __APP_VERSION__,
        },
        // Longer than a normal call. A terminal that has just regained a weak
        // signal is exactly the case where a fifteen-second timeout turns a
        // successful upload into a retry.
        //
        // The device token vouches for the `cashier_id` on each operation, so
        // a sale is credited to whoever rang it up rather than to whoever is
        // signed in now (`_cashier_for` in apps/sync/services.py).
        {
          timeoutMs: 60_000,
          headers: await deviceHeaders(),
          // A 401 here is about the till's pairing, not anybody's session.
          authenticatedByDevice: !asUser,
        },
      );
    } catch (error) {
      // The **batch** failed — nothing was judged, so nothing is acknowledged.
      // Every operation goes back with its attempt counted, exactly as it would
      // have done one at a time.
      await failWholeBatch(claimed, error, result);
      await log("push", result);
      return result;
    }

    await recordVerdicts(claimed, response, result);

    if (result.acknowledged > 0) {
      await markSynced();
    }
    await updateSyncMeta({
      clockSkewMs: new Date(response.server_time).getTime() - Date.now(),
    });
    await log("push", result);
    return result;
  } finally {
    running = false;
  }
}

/**
 * Map each verdict back onto its outbox row.
 *
 * By `op_id`, not by position: a response that only reported totals would leave
 * the client guessing which three of five landed, and guessing wrong in the
 * optimistic direction loses a sale.
 */
async function recordVerdicts(
  claimed: OutboxOperation[],
  response: PushResponse,
  result: SyncResult,
): Promise<void> {
  const verdicts = new Map(response.results.map((row) => [row.op_id, row]));

  for (const operation of claimed) {
    const verdict = verdicts.get(operation.opId);

    if (!verdict) {
      // The server did not mention it. Not an acknowledgement, so it stays
      // queued — the one thing that must never happen is local data leaving on
      // the strength of a response that did not cover it.
      await outboxRepository.retryLater(operation.opId, "The server did not answer for this.");
      result.retried += 1;
      continue;
    }

    if (verdict.outcome === "REJECTED") {
      await outboxRepository.conflict(operation.opId, verdict.detail, {
        error_type: verdict.error_type,
      });
      result.conflicted += 1;
      continue;
    }

    // APPLIED and DUPLICATE are both successes. The device retried because it
    // did not know whether the first attempt landed; "it did, here it is" is the
    // answer, and treating it as an error is how a retry becomes a second sale.
    await outboxRepository.acknowledge(operation.opId);
    result.acknowledged += 1;
  }
}

/**
 * The request itself failed, so no operation was judged.
 *
 * Everything goes back to the queue. The distinction between "the server said
 * no" and "the server never answered" is the whole reason a queue exists.
 */
async function failWholeBatch(
  claimed: OutboxOperation[],
  error: unknown,
  result: SyncResult,
): Promise<void> {
  const detail = describe(error);
  const transient = isTransient(error);
  for (const operation of claimed) {
    await outboxRepository.retryLater(operation.opId, detail, { transient });
    result.retried += 1;
  }
}

/**
 * A failure that says nothing about the operations themselves: no signal, a
 * login to renew, a rate limit, a server that is down. These must never
 * dead-letter a queue — a till back from three weeks offline would otherwise
 * find every sale FAILED and stop trying the moment it could succeed.
 */
function isTransient(error: unknown): boolean {
  if (error instanceof NetworkError) return true;
  if (error instanceof ApiError) {
    return [401, 403, 408, 429].includes(error.status) || error.status >= 500;
  }
  return false;
}

function describe(error: unknown): string {
  if (error instanceof NetworkError) return "The server could not be reached.";
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 403) {
      // A stale session is fixed by signing in again. Asking a shopkeeper to
      // adjudicate a login would be absurd.
      return "Sign in again to finish uploading.";
    }
    return error.detail || error.message;
  }
  return String(error).slice(0, 200);
}

// ═════════════════════════════════════════════════════════════════════════════
// Pull
// ═════════════════════════════════════════════════════════════════════════════
interface CatalogueFeed {
  cursor: string;
  truncated: boolean;
  products: {
    id: string;
    name: string;
    sku: string | null;
    barcodes: string[];
    category_id: string | null;
    selling_price: string;
    tax_rate: string;
    unit_code: string;
    search_keywords: string;
    track_stock: boolean;
    is_active: boolean;
    updated_at: string;
    image_url: string | null;
    image_thumb_url: string | null;
  }[];
  categories: { id: string; name: string; colour: string; sort_order: number }[];
  stock: { product_id: string; section_id: string; quantity: string }[];
  settings?: {
    offline_grant_days: number;
    offline_refund_limit: string;
    require_approval_for_refunds: boolean;
  };
  server_time: string;
}

/**
 * Refresh the local copy of the catalogue.
 *
 * Upserts by id, so re-fetching the overlapping edge of the cursor window costs
 * nothing. Loops while the server reports a truncated page, so a first sync of a
 * large catalogue completes rather than stopping at two thousand products and
 * leaving half the shelf unscannable.
 */
export async function pull(): Promise<{ products: number; rounds: number }> {
  const session = await db.auth.get("current");
  if (session?.offline) return { products: 0, rounds: 0 };
  // No shop, no catalogue: a stokvel member's own account, or SmartSpaza staff
  // outside a support view. Asking anyway is a refused request a minute, on
  // what is often a prepaid bundle.
  if (!session?.merchant) return { products: 0, rounds: 0 };
  const merchantId = session.merchant.id;
  // One cursor per counter. The stock figures in the feed are for the section
  // this session is working in, so a cashier who moves to another counter needs
  // a full pull of that counter's balances, not the changes since the last one.
  const cursorKey = `catalogue:${session?.activeSectionId ?? "all"}`;

  let products = 0;
  let rounds = 0;

  for (;;) {
    const meta = await getSyncMeta();
    const since = meta.cursors[cursorKey];

    const feed = await api.get<CatalogueFeed>(
      `/sync/catalogue/${since ? `?since=${encodeURIComponent(since)}` : ""}`,
      { timeoutMs: 30_000 },
    );

    await db.transaction(
      "rw",
      db.products,
      db.categories,
      db.stockLevels,
      db.syncMeta,
      async () => {
        await db.products.bulkPut(
          feed.products.map((row) => ({
            id: row.id,
            merchantId,
            name: row.name,
            sku: row.sku,
            barcodes: row.barcodes,
            categoryId: row.category_id,
            sellingPrice: row.selling_price,
            taxRate: row.tax_rate,
            unitOfMeasure: row.unit_code,
            searchKeywords: row.search_keywords,
            imageUrl: row.image_url,
            imageThumbUrl: row.image_thumb_url,
            trackStock: row.track_stock,
            isActive: row.is_active,
            version: 0,
            updatedAt: row.updated_at,
          })),
        );

        await db.categories.bulkPut(
          feed.categories.map((row) => ({
            id: row.id,
            merchantId,
            name: row.name,
            colour: row.colour,
            sortOrder: row.sort_order,
            version: 0,
          })),
        );

        // `serverQty` only. `pendingDelta` is this device's own unsynced
        // movements, and the server has no idea about those — overwriting it here
        // would make the till forget the sales it has not yet uploaded and show a
        // shelf quantity that is too high.
        await Promise.all(
          feed.stock.map((row) =>
            localStockRepository.receive(row.section_id, row.product_id, row.quantity),
          ),
        );

        await updateSyncMeta({
          cursors: { ...meta.cursors, [cursorKey]: feed.cursor },
          ...(feed.settings
            ? {
                tillSettings: {
                  offlineGrantDays: feed.settings.offline_grant_days,
                  offlineRefundLimit: feed.settings.offline_refund_limit,
                  refundsNeedApproval: feed.settings.require_approval_for_refunds,
                },
              }
            : {}),
          lastSuccessfulSyncAt: new Date().toISOString(),
          // A POS terminal's clock drifts, and the business date depends on it.
          // Measured rather than corrected: the device clock stays authoritative
          // for `client_created_at`, and the skew is what support looks at when a
          // day's takings land on the wrong day.
          clockSkewMs: new Date(feed.server_time).getTime() - Date.now(),
        });
      },
    );

    products += feed.products.length;
    rounds += 1;

    // A bounded loop. A server that always reported `truncated` — a bug, or a
    // clock moving backwards — must not spin a till's battery flat.
    if (!feed.truncated || rounds >= 20) break;
  }

  await log("pull", { attempted: products, acknowledged: products, retried: 0, conflicted: 0 });
  return { products, rounds };
}

/** Push, then pull. What "sync now" means. */
export async function syncNow(): Promise<{ push: SyncResult; pulled: number }> {
  const pushed = await push();
  const { products } = await pull();
  return { push: pushed, pulled: products };
}

async function deviceHeaders(): Promise<Record<string, string>> {
  const token = await deviceRepository.getToken();
  return token ? { "X-SmartSpaza-Device-Token": token } : {};
}

async function markSynced(): Promise<void> {
  await updateSyncMeta({ lastSuccessfulSyncAt: new Date().toISOString() });
}

async function log(direction: "push" | "pull", result: SyncResult): Promise<void> {
  try {
    await db.syncLog.add({
      at: Date.now(),
      direction,
      ok: result.conflicted === 0,
      operationCount: result.attempted,
      message:
        result.conflicted > 0
          ? `${result.conflicted} needing attention`
          : `${result.acknowledged} of ${result.attempted}`,
    });
    // Capped. A rolling diagnostic is useful; an unbounded one on a device with
    // a few hundred megabytes of quota is a slow leak.
    const excess = (await db.syncLog.count()) - 200;
    if (excess > 0) {
      const oldest = await db.syncLog.orderBy("at").limit(excess).primaryKeys();
      await db.syncLog.bulkDelete(oldest);
    }
  } catch {
    // A diagnostic log must never be the reason a sale fails to upload.
  }
}
